import axios from 'axios';
import Api from '../api/userApi';

const VISITOR_KEY = 'ulca_visitor_id';
const SESSION_KEY = 'ulca_session_id';

const genId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (_) {
    /* ignore */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// Stable id per browser (counts unique visitors)
const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = genId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch (_) {
    return undefined;
  }
};

// Detect how the app is currently being used:
//  - 'standalone' => launched from an installed PWA (home screen / app window)
//  - 'browser'    => running inside a normal browser tab
const getDisplayMode = () => {
  try {
    const standaloneModes = ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay'];
    const isStandalone =
      standaloneModes.some((m) => window.matchMedia?.(`(display-mode: ${m})`).matches) ||
      ('standalone' in window.navigator && window.navigator.standalone);
    return isStandalone ? 'standalone' : 'browser';
  } catch (_) {
    return 'browser';
  }
};

// Whether the PWA has been installed on this device. True if we are currently
// running as an installed app, or if a previous install was recorded.
const getPwaInstalled = () => {
  try {
    if (getDisplayMode() === 'standalone') return true;
    return localStorage.getItem('pwa-installed') === 'true';
  } catch (_) {
    return false;
  }
};

// Id per tab/session (counts sessions)
const getSessionId = () => {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = genId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (_) {
    return undefined;
  }
};

/**
 * Fire-and-forget page view tracking. Records the visitor's device, OS,
 * browser (derived server-side from the UA) plus screen size and path.
 * Failures are swallowed so analytics never affects the user experience.
 */
export const trackPageView = (path) => {
  try {
    const payload = {
      visitorId: getVisitorId(),
      sessionId: getSessionId(),
      screenWidth: window.screen?.width,
      screenHeight: window.screen?.height,
      path: path || window.location?.pathname || '/',
      referrer: document.referrer || '',
      language: navigator.language || '',
      displayMode: getDisplayMode(),
      pwaInstalled: getPwaInstalled(),
    };
    // Send the auth token if logged in so the device can be tied to the user
    let config;
    try {
      const token = localStorage.getItem('token');
      if (token) config = { headers: { Authorization: `Bearer ${token}` } };
    } catch (_) {
      /* ignore */
    }
    axios.post(Api.visitorTrack, payload, config).catch(() => {});
  } catch (_) {
    /* never throw from tracking */
  }
};

export default trackPageView;
